import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Center,
  Collapse,
  Container,
  Group,
  Loader,
  Modal,
  Notification,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  createOrUpdateDefaultConfig,
  getDefaultConfigs,
} from "@utils/api-communication/RestApiCli";
import { ManagementConfig, ProductConfig } from "types/productManagementTypes";
import ManagementConfigForm, {
  emptyManagementConfig,
  mergeStoredConfig,
} from "./ManagementConfigForm";
import ConfigJsonView from "./ConfigJsonView";
import DefaultConfigDevices from "./DefaultConfigDevices";

type EditMode = "create" | "edit" | "duplicate";

type EditState = {
  mode: EditMode;
  hardwareModel: string;
  puiVersion: string;
  version: string;
  config: ManagementConfig;
  // Original key of the config being duplicated. The save is keyed on
  // (hardwareModel, puiVersion, version) and upserts, so a duplicate that
  // keeps all three would overwrite the source instead of creating a new one.
  source?: { hardwareModel: string; puiVersion: string; version: string };
};

const blankEdit = (): EditState => ({
  mode: "create",
  hardwareModel: "",
  puiVersion: "",
  version: "",
  config: emptyManagementConfig(),
});

const DefaultConfigs: React.FC = () => {
  const [configs, setConfigs] = useState<ProductConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  const fetchConfigs = () => {
    setLoading(true);
    getDefaultConfigs(
      (response) => {
        setConfigs(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      },
      () => {
        setNotification({ type: "error", msg: "Failed to fetch default configs" });
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const startCreate = () => setEditState(blankEdit());

  const startEdit = (cfg: ProductConfig) => {
    setEditState({
      mode: "edit",
      hardwareModel: cfg.hardwareModel ?? "",
      puiVersion: cfg.puiVersion ?? "",
      version: cfg.version ?? "",
      config: mergeStoredConfig(cfg.config),
    });
  };

  const startDuplicate = (cfg: ProductConfig) => {
    const key = {
      hardwareModel: cfg.hardwareModel ?? "",
      puiVersion: cfg.puiVersion ?? "",
      version: cfg.version ?? "",
    };
    setEditState({
      mode: "duplicate",
      ...key,
      config: mergeStoredConfig(cfg.config),
      source: key,
    });
  };

  const requiredFieldsFilled = (s: EditState) =>
    s.hardwareModel.trim() !== "" && s.puiVersion.trim() !== "" && s.version.trim() !== "";

  const keyDiffersFromSource = (s: EditState) =>
    !s.source ||
    s.hardwareModel.trim() !== s.source.hardwareModel.trim() ||
    s.puiVersion.trim() !== s.source.puiVersion.trim() ||
    s.version.trim() !== s.source.version.trim();

  const handleSave = () => {
    if (!editState) {
      return;
    }
    if (!requiredFieldsFilled(editState)) {
      setNotification({
        type: "error",
        msg: "Hardware model, PUI version, and app version are required",
      });
      return;
    }
    if (!keyDiffersFromSource(editState)) {
      setNotification({
        type: "error",
        msg: "Change the hardware model, PUI version, or app version so the duplicate does not overwrite the original",
      });
      return;
    }
    setSaving(true);
    createOrUpdateDefaultConfig(
      {
        hardwareModel: editState.hardwareModel.trim(),
        puiVersion: editState.puiVersion.trim(),
        version: editState.version.trim(),
        config: editState.config as unknown as Record<string, unknown>,
      },
      () => {
        setSaving(false);
        setEditState(null);
        setNotification({ type: "success", msg: "Default config saved" });
        fetchConfigs();
      },
      () => {
        setSaving(false);
        setNotification({ type: "error", msg: "Failed to save default config" });
      },
    );
  };

  if (loading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  const rowKey = (config: ProductConfig, idx: number) =>
    `${config.hardwareModel ?? "?"}-${config.puiVersion ?? "?"}-${config.version ?? "?"}-${idx}`;

  return (
    <Container>
      <Group justify="space-between" align="center" my="md">
        <Title order={2}>Default Configurations</Title>
        <Button onClick={startCreate}>Create Default Config</Button>
      </Group>

      {notification && (
        <Notification
          color={notification.type === "success" ? "green" : "red"}
          onClose={() => setNotification(null)}
          withCloseButton
          mb="md"
        >
          {notification.msg}
        </Notification>
      )}

      <Paper withBorder radius="sm" p={0}>
        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Hardware</Table.Th>
              <Table.Th>PUI Version</Table.Th>
              <Table.Th>App Version</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {configs.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" py="sm">
                    No default configs found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              configs.map((config, idx) => {
                const key = rowKey(config, idx);
                return (
                  <React.Fragment key={key}>
                    <Table.Tr>
                      <Table.Td>
                        <Badge variant="light" color="blue">
                          {config.hardwareModel || "-"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{config.puiVersion || "-"}</Table.Td>
                      <Table.Td>{config.version || "-"}</Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {config.date ? new Date(config.date).toLocaleString() : "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => setExpandedRow(expandedRow === key ? null : key)}
                          >
                            {expandedRow === key ? "Hide" : "Show"}
                          </Button>
                          <Button size="xs" onClick={() => startEdit(config)}>
                            Edit
                          </Button>
                          <Button size="xs" variant="light" onClick={() => startDuplicate(config)}>
                            Duplicate
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td colSpan={5} p={0}>
                        <Collapse in={expandedRow === key}>
                          <Stack gap="md" style={{ padding: "var(--mantine-spacing-md)" }}>
                            <div>
                              <Text fw={600} size="sm" mb="xs">
                                Devices in this group
                              </Text>
                              {config.puiVersion && config.version ? (
                                <DefaultConfigDevices
                                  hardwareModel={config.hardwareModel}
                                  puiVersion={config.puiVersion}
                                  version={config.version}
                                />
                              ) : (
                                <Text size="sm" c="dimmed">
                                  This default config is missing PUI version or app version, so
                                  matching devices cannot be resolved.
                                </Text>
                              )}
                            </div>
                            <div>
                              <Text fw={600} size="sm" mb="xs">
                                Configuration
                              </Text>
                              <ConfigJsonView value={config.config} />
                            </div>
                          </Stack>
                        </Collapse>
                      </Table.Td>
                    </Table.Tr>
                  </React.Fragment>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Modal
        opened={!!editState}
        onClose={() => (saving ? null : setEditState(null))}
        title={
          editState?.mode === "create"
            ? "Create Default Config"
            : editState?.mode === "duplicate"
              ? "Duplicate Default Config"
              : "Edit Default Config"
        }
        size="xl"
        closeOnClickOutside={!saving}
        closeOnEscape={!saving}
      >
        {editState && (
          <Stack gap="md">
            {editState.mode === "duplicate" && (
              <Alert color="blue" variant="light" title="Duplicating default config">
                Change the hardware model, PUI version, or app version below. A duplicate that keeps
                all three would overwrite the original config instead of creating a new one.
              </Alert>
            )}
            <Group grow>
              <TextInput
                label="Hardware model"
                placeholder="pn3, p4e"
                value={editState.hardwareModel}
                onChange={(e) =>
                  setEditState({ ...editState, hardwareModel: e.currentTarget.value })
                }
                disabled={editState.mode === "edit" || saving}
              />
              <TextInput
                label="PUI version"
                placeholder="1.2.3"
                value={editState.puiVersion}
                onChange={(e) => setEditState({ ...editState, puiVersion: e.currentTarget.value })}
                disabled={editState.mode === "edit" || saving}
              />
              <TextInput
                label="App version"
                placeholder="2.5.1"
                value={editState.version}
                onChange={(e) => setEditState({ ...editState, version: e.currentTarget.value })}
                disabled={editState.mode === "edit" || saving}
              />
            </Group>
            <ManagementConfigForm
              value={editState.config}
              onChange={(config) => setEditState({ ...editState, config })}
              disabled={saving}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditState(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} loading={saving}>
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default DefaultConfigs;
